import {assert} from 'Asserts';
import {RecipesFeature, AssaysFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  PROCESS_EXECUTION_DISPLAY_NAME_FOR_PROCESSES,
  PROCESS_EXECUTION_DISPLAY_NAME_FOR_RECIPES,
  PROCESS_EXECUTION_DISPLAY_NAME_FOR_ASSAYS,
} from 'graphql-client/__generated/GeneratedEnumMaps';
import {
  ProcessExecutionType,
  ProcessTaskStatusType,
  CreationOriginType,
  ProcessPlatformFeatureType,
  ProcessClientOrigin,
  ProcessDisplayNameProcesses,
  ProcessDisplayNameRecipes,
  ProcessDisplayNameAssays,
  ProcedureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DoesEvaluatedTableHaveAnyErrorsOptions} from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {Organization} from 'orgs/OrganizationConstants';
import {getProcessPlatformFeatureConfig} from 'processes/processes-platform/FeatureOptions';
import {Schema} from 'schemas/SchemaConstants';
import {FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import strUtil from 'util/strUtil';
import {assertNever} from 'util/Types';

import {ProcessTaskStatusLifecycleGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskStatusLifecycleGraphQL';
import {TaskFinderLookupConfig} from './__generated/TaskFinderLookupConfig';

export const NO_ACCESS_TEXT = '(No access)';

// Keep in sync with benchling/processes/entry_execution/constants.py
export const PROCESS_WIDER_COLUMN_WIDTH = 150;

export const PROCESS_DASHBOARD_TASK_STATUS_COLUMN_WIDTH = 330;

const IN_DEVELOPMENT_EXECUTION_TYPES = [ProcessExecutionType.PIPELINE] as const;

type InDevelopmentExecutionType = (typeof IN_DEVELOPMENT_EXECUTION_TYPES)[number];

export function isInDevelopmentExecutionType(
  executionType: ProcessExecutionType
): executionType is InDevelopmentExecutionType {
  return IN_DEVELOPMENT_EXECUTION_TYPES.includes(executionType as InDevelopmentExecutionType);
}

export const creationOriginTypeToDisplayName: {[originType in CreationOriginType]: string} = {
  [CreationOriginType.DIRECT_EXECUTION_MODAL]: 'Direct execution modal',
  [CreationOriginType.ENTRY]: 'Notebook entry',
  [CreationOriginType.PROCESS]: 'Task group',
  [CreationOriginType.PROCESSES_REQUEST_MIGRATION]: 'Migrated from Request',
  [CreationOriginType.PROCESSES_SCHEMA_FIELD_MAPPER]: 'Flowchart path',
  [CreationOriginType.STAGE_ENTRY]: 'Stage entry',
  [CreationOriginType.REQUEST_V2_SUBMISSION]: 'Request',
};

export const clientOriginToDisplayName: Record<ProcessClientOrigin, string> = {
  [ProcessClientOrigin.PROCESSES]: 'Workflows',
  [ProcessClientOrigin.RECIPES]: 'Recipe execution',
  [ProcessClientOrigin.ASSAYS]: 'Assay execution',
  [ProcessClientOrigin.DISCREPANCIES]: 'Discrepancies',
};

export const clientOriginToProcedureType: Record<ProcessClientOrigin, ProcedureType | null> = {
  [ProcessClientOrigin.RECIPES]: ProcedureType.RECIPE,
  [ProcessClientOrigin.ASSAYS]: ProcedureType.ASSAY,
  [ProcessClientOrigin.PROCESSES]: null,
  [ProcessClientOrigin.DISCREPANCIES]: null,
};

// Process dashboard
export const PROCESS_DASHBOARD_ID_COLUMN_WIDTH = 200;

// keep in sync with benchling/processes/default_process_task_status_lifecycle.py
export enum DefaultStatusDisplayName {
  PENDING = 'Pending',
  PLANNED = 'Planned',
  IN_PROGRESS = 'In Progress',
  FAILED = 'Failed',
  CANCELLED = 'Cancelled',
  INVALID = 'Invalid',
  COMPLETED = 'Completed',
  COMPLETED_AFTER_REVIEW = 'Completed (after review)',
  ACTIVE = 'Active',
  SKIPPED = 'Skipped',
  NOT_READY = 'Not Ready',
  READY = 'Ready',
}

export function isProcessTaskStatus(s: string): s is ProcessTaskStatusType {
  return Object.keys(ProcessTaskStatusType).includes(s);
}

export function getEnabledClientOrigins(): Array<ProcessClientOrigin> {
  const clientOrigins = [ProcessClientOrigin.PROCESSES];
  if (isFeatureEnabled({feature: RecipesFeature.RECIPES_OPEN})) {
    clientOrigins.push(ProcessClientOrigin.RECIPES);
  }

  if (BenchlingConfig.PROCEDURES_ENABLE_ASSAYS && isFeatureEnabled({feature: AssaysFeature.ASSAYS_OPEN})) {
    clientOrigins.push(ProcessClientOrigin.ASSAYS);
  }
  return clientOrigins;
}
// This function returns user-friendly names for execution types. Note that
// this doesn't necessarily mean it can function as a standalone noun - you
// wouldn't refer to a task in the UI as just "direct".
export function processExecutionTypeDisplayName(
  clientOrigin: ProcessClientOrigin,
  executionType: ProcessExecutionType
): string {
  switch (clientOrigin) {
    case ProcessClientOrigin.PROCESSES:
    case ProcessClientOrigin.DISCREPANCIES:
      return PROCESS_EXECUTION_DISPLAY_NAME_FOR_PROCESSES[
        executionType as unknown as ProcessDisplayNameProcesses
      ];
    case ProcessClientOrigin.RECIPES: {
      return PROCESS_EXECUTION_DISPLAY_NAME_FOR_RECIPES[
        executionType as unknown as ProcessDisplayNameRecipes
      ];
    }
    case ProcessClientOrigin.ASSAYS: {
      return PROCESS_EXECUTION_DISPLAY_NAME_FOR_ASSAYS[executionType as unknown as ProcessDisplayNameAssays];
    }

    default:
      assert(false, `Unexpected client origin: ${assertNever(clientOrigin)}`);
  }
}

// Use this function instead of iterating through ProcessExecutionType enum for user features
// Keep in sync with the corresponding function in benchling/processes/models/process_task_schema.py
export function getEnabledProcessExecutionTypes(): Array<ProcessExecutionType> {
  const proceduresOpenEnabled =
    isFeatureEnabled({feature: RecipesFeature.RECIPES_OPEN}) ||
    isFeatureEnabled({feature: AssaysFeature.ASSAYS_OPEN});

  const conditionalExecutionTypes: {[key in ProcessExecutionType]?: boolean} = {
    [ProcessExecutionType.PROCEDURE_METHOD]: proceduresOpenEnabled,
    [ProcessExecutionType.PROCEDURE]: proceduresOpenEnabled,
    [ProcessExecutionType.PROCEDURE_STEP]: proceduresOpenEnabled,
    [ProcessExecutionType.PIPELINE]: BenchlingConfig.WORKFLOWS_ENABLE_PIPELINE_NODES,
  };
  return Object.values(ProcessExecutionType).filter(
    (executionType) => conditionalExecutionTypes[executionType] ?? true
  );
}

export function getEnabledProcessExecutionTypesForPlatformFeature(
  feature: ProcessPlatformFeatureType
): Array<ProcessExecutionType> {
  return getEnabledProcessExecutionTypes().filter((executionType) =>
    getProcessPlatformFeatureConfig(feature, executionType)
  );
}

// Keep in sync with the corresponding function in benchling/processes/models/process_task_status.py
export function statusTypeToDisplayName(statusType: string): string | null {
  if (isProcessTaskStatus(statusType)) {
    return strUtil.toSentenceCase(statusType).replace(/_/g, '-');
  }
  return null;
}

// The process tables are used in several locations, each of which has different table behavior.
// Keep in sync with benchling/processes/entry_execution/constants.py
export const enum ProcessTableLocation {
  CREATE_MODAL = 'CREATE_MODAL',
  WORKSPACE_TAB = 'WORKSPACE_TAB',
  ENTRY = 'ENTRY',
  STATUS_CONFIRMATION_MODAL = 'STATUS_CONFIRMATION_MODAL',
  DIRECT_EXECUTION_MODAL = 'DIRECT_EXECUTION_MODAL',
  FIELD_MAPPING_MODAL = 'FIELD_MAPPING_MODAL',
  TASK_DETAILS_OUTPUTS_TAB = 'TASK_DETAILS_OUTPUTS_TAB',
  REQUEST_V2_DOCUMENT = 'REQUEST_V2_DOCUMENT',
  REQUEST_V2_SUBMISSION_DETAILS_PAGE = 'REQUEST_V2_SUBMISSION_DETAILS_PAGE',
}

export type MappableProcessType = ModelTypeConstants.PROCESS_TASK | ModelTypeConstants.PROCESS_OUTPUT;

export type ProcessManagementItemType =
  | ModelTypeConstants.PROCESS
  | ModelTypeConstants.PROCESS_TASK
  | ModelTypeConstants.PROCESS_OUTPUT;

export const PROCESS_MANAGEMENT_ITEM_TYPE_TO_READABLE: {[key in ProcessManagementItemType]: string} = {
  [ModelTypeConstants.PROCESS]: 'task group',
  [ModelTypeConstants.PROCESS_TASK]: 'task',
  [ModelTypeConstants.PROCESS_OUTPUT]: 'output',
};

export const UNASSIGNED_TASK_TEXT = 'Unassigned';

export const TASK_WITHOUT_RESPONSIBLE_PARTIES_TEXT = 'No responsible parties set';

interface ProcessSchemaSummary {
  id: string;
  name: string;
  sqlIdentifier: string;
}

export interface ProcessTaskSchema extends ProcessSchemaSummary, Schema {
  __typename: 'ProcessTaskSchemaNode';
  organization: Organization;
  prefix: string;
  statusLifecycle: ProcessTaskStatusLifecycleGraphQL;
  executionType: ProcessExecutionType;
  canSetAssigneeOnTaskCreation: boolean;
}

export interface ProcessOutputSchema extends ProcessSchemaSummary, Schema {
  organization: Organization;
  prefix: string;
  taskFinderLookupConfig: TaskFinderLookupConfig | null;
  processExecutionType: ProcessExecutionType;
  processTaskSchemaName: string;
}

// Styling for icons and associated tooltips in schema config UI
export const HELP_TOOLTIP_STYLE = {
  fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  marginLeft: 6,
};

export enum DefaultTaskGroupInstructionsLines {
  CREATE_MODAL = 2,
  WORKSPACE_TAB = 4,
}

export const EVALUATED_TABLE_ERROR_OPTIONS: DoesEvaluatedTableHaveAnyErrorsOptions = {
  ignoreLoadingErrors: true,
  ignoreWarnings: true,
};
